// UI labels, descriptions, and action buttons for operations an agent can own.
// Mirrors the backend OPERATION_META; this admin copy is the source for UI
// labels, the picker, and the desk action strip.
//
// To add an action: add an entry to its `actions` array.
// To add an action with custom UI (e.g. a date-picker popover): provide a
// `Trigger` component — it renders the button + any popover/dialog itself.
// Without `Trigger`, OperationBlock renders a plain button that calls `run`.

import type { ComponentType } from 'react';

export interface OperationActionTriggerProps {
  soulId: string;
  onSuccess: () => void;
}

export interface OperationAction {
  id: string;
  label: string;
  description?: string;
  // Optional custom trigger — renders its own button + popover/dialog.
  // When absent, OperationBlock renders a plain outline button calling `run`.
  Trigger?: ComponentType<OperationActionTriggerProps>;
  // Used only when Trigger is absent.
  run?: (soulId: string) => Promise<void>;
}

export interface OperationMeta {
  label: string;
  description: string;
  // Dispositional "voice" lines for an agent owning this operation — always-true
  // statements about what it watches, not claims that a job is running.
  presenceLines?: string[];
  actions?: OperationAction[];
}

// Imported lazily to avoid circular deps — the component file imports service,
// which is fine; we just need to reference the type here.
import RunShiftReportTrigger from '../views/souls/desk/RunShiftReportTrigger';
import RunEventReportTrigger from '../views/souls/desk/RunEventReportTrigger';
import RunWeddingReportTrigger from '../views/souls/desk/RunWeddingReportTrigger';
import RunTourReportTrigger from '../views/souls/desk/RunTourReportTrigger';
import RunHotelReportTrigger from '../views/souls/desk/RunHotelReportTrigger';
import RunLapseDetectionTrigger from '../views/souls/desk/RunLapseDetectionTrigger';

export const OPERATION_META: Record<string, OperationMeta> = {
  SHIFT_REPORT: {
    label: 'Shift Reports',
    description: 'Owns the venue shift-report operation.',
    presenceLines: [
      'On the clock across every venue',
      'Remembering your regulars',
      'Watching for VIPs and special occasions',
      'Keeping an eye on no-show risks',
    ],
    actions: [
      {
        id: 'run-shift-report',
        label: 'Run Shift Report',
        description: 'Trigger a fresh report across all venues now.',
        Trigger: RunShiftReportTrigger,
      },
    ],
  },
  EVENT_REPORT: {
    label: 'Event Reports',
    description: 'Owns post-event recaps of past ticketed events.',
    presenceLines: [
      'Watching how events land',
      'Counting the house',
      'Tracking sell-through across the calendar',
    ],
    actions: [
      {
        id: 'run-event-report',
        label: 'Run Event Recap',
        description: 'Generate recaps for recently-ended ticketed events now.',
        Trigger: RunEventReportTrigger,
      },
    ],
  },
  WEDDING_REPORT: {
    label: 'Wedding Bookings',
    description: 'Owns the digest of weddings that went definite.',
    presenceLines: [
      'Watching the wedding book',
      'Tracking what goes definite',
      'Getting to know the couples who booked',
      'Keeping an eye on deposits and dates',
    ],
    actions: [
      {
        id: 'run-wedding-rollup',
        label: 'Run Wedding Rollup',
        description: 'Roll up the weddings won in a date range now.',
        Trigger: RunWeddingReportTrigger,
      },
    ],
  },
  TOUR_REPORT: {
    label: 'Wedding Tours',
    description: 'Owns the digest of upcoming wedding tours.',
    presenceLines: [
      'Watching the tour calendar',
      'Tracking who is coming in for a tour',
      'Getting to know the couples touring',
      'Keeping an eye on upcoming tour dates',
    ],
    actions: [
      {
        id: 'run-tour-rollup',
        label: 'Run Tour Rollup',
        description: 'Roll up the upcoming wedding tours in a date range now.',
        Trigger: RunTourReportTrigger,
      },
    ],
  },
  HOTEL_REPORT: {
    label: 'Hotel Bookings',
    description: 'Owns the digest of hotel stays that got booked.',
    presenceLines: [
      'Watching the hotel book',
      'Tracking rooms and room-nights',
      'Getting to know the guests who booked',
      'Keeping an eye on revenue and repeat stays',
    ],
    actions: [
      {
        id: 'run-hotel-rollup',
        label: 'Run Hotel Rollup',
        description: 'Roll up the hotel stays booked in a date range now.',
        Trigger: RunHotelReportTrigger,
      },
    ],
  },
  LAPSE_DETECTION: {
    label: 'Flow Lapses',
    description: 'Owns the flow lapse-detection sweep — members who go quiet.',
    presenceLines: [
      'Watching for members who go quiet',
      'Sweeping flows for stalled fish',
      'Noticing who never converted',
      'Catching lapses before they drift',
    ],
    actions: [
      {
        id: 'run-lapse-detection',
        label: 'Run Lapse Sweep',
        description: 'Scan all lapse stages now and emit any lapse events found.',
        Trigger: RunLapseDetectionTrigger,
      },
    ],
  },
};

export const OPERATION_KEYS = Object.keys(OPERATION_META);

/**
 * The "voice" lines for an agent owning these operations — the union of what each
 * one watches for, so a persona only says things true of the work it does.
 */
export const presenceLinesFor = (operations: string[] = []): string[] =>
  (operations ?? []).flatMap((key) => OPERATION_META[key]?.presenceLines ?? []);
